"use client";

import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const categories = [{
  id: "all",
  label: "TODOS"
}, {
  id: "estructurales",
  label: "ESTRUCTURALES"
}, {
  id: "planos",
  label: "PLANOS"
}, {
  id: "tubería",
  label: "TUBERÍA"
}, {
  id: "perfilería",
  label: "PERFILERÍA"
}];
const products = [{
  id: 1,
  category: "estructurales",
  eyebrow: "ACERO · ALTA RESISTENCIA",
  name: "Viga IPR Estructural",
  nameItalic: "IPR",
  description: "Vigas de acero laminadas en caliente, ideales para soportar grandes cargas en naves industriales.",
  price: "Cotizar",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/viga_ipr_acero.png",
  imageHover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/viga_ipr_obra.png",
  specs: "Material: Acero A-36 o A-572-50 · Longitud: 6.10m y 12.20m · Aplicaciones: Edificios, puentes y estructuras pesadas."
}, {
  id: 2,
  category: "planos",
  eyebrow: "LÁMINA · RECUBIERTA",
  name: "Lámina Galvanizada R-101",
  nameItalic: "R-101",
  description: "Perfil trapezoidal diseñado para cubiertas y muros de naves industriales con excelente drenaje pluvial.",
  price: "Cotizar",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/lamina_galvanizada_stock.png",
  imageHover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/lamina_galvanizada_instalacion.png",
  specs: "Calibres: 22 al 28 · Acabado: Galvanizado o Zintro Alum · Ancho efectivo: 101 cm · Alta resistencia a la corrosión."
}, {
  id: 3,
  category: "tubería",
  eyebrow: "TUBERÍA · CÉDULA 40",
  name: "Tubo de Acero Negro",
  nameItalic: "Negro",
  description: "Tubería de conducción de fluidos y aplicaciones estructurales con soldadura de alta frecuencia.",
  price: "Cotizar",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/tubo_acero_negro.png",
  imageHover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/tubo_acero_almacen.png",
  specs: "Norma: ASTM A-53 · Diámetros: 1/2\" a 4\" · Extremos: Lisos o roscados · Uso: Conducción de gas, agua o vapor."
}, {
  id: 4,
  category: "perfilería",
  eyebrow: "PERFIL · COMERCIAL",
  name: "Ángulo de Acero",
  nameItalic: "Ángulo",
  description: "Producto de sección transversal en forma de L, esencial para marcos, herrería y refuerzos.",
  price: "Cotizar",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/angulo_acero_detalle.png",
  imageHover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/angulo_acero_herreria.png",
  specs: "Grado: A-36 · Espesores: 1/8\" a 1\" · Longitud: 6.10m · Versatilidad total para proyectos de construcción ligera."
}, {
  id: 5,
  category: "estructurales",
  eyebrow: "CANAL · LAMINADO",
  name: "Canal CPS (UPE)",
  nameItalic: "CPS",
  description: "Perfil en forma de U utilizado principalmente en la fabricación de carrocerías y estructuras de soporte.",
  price: "Cotizar",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/canal_cps_acero.png",
  imageHover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/canal_cps_aplicacion.png",
  specs: "Material: Acero estructural · Medidas: 3\" a 12\" · Acabado: Negro · Ideal para trabes y canales de conducción."
}, {
  id: 6,
  category: "planos",
  eyebrow: "PLACA · GRADO A-36",
  name: "Placa de Acero en Hoja",
  nameItalic: "Placa",
  description: "Placas de acero de alta ductilidad para procesos de corte, doblez y soldadura en la industria metalmecánica.",
  price: "Cotizar",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/placa_acero_hoja.png",
  imageHover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/placa_acero_corte.png",
  specs: "Espesores: 3/16\" a 4\" · Dimensiones: 3'x10', 4'x10', 5'x20' · Certificación de molino disponible bajo pedido."
}];
export default function SubProductsGrid() {
  const [activeCategory, setActiveCategory] = useState("all");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const filtered = activeCategory === "all" ? products : products.filter(p => p.category === activeCategory);
  return <section data-section-id="2749" id="sub-products-grid" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase mb-8 text-primary">
            Catálogo de Soluciones
          </p>
          <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.1] max-w-[900px] mx-auto text-foreground">
            Materiales <em className="italic font-medium text-accent">forjados</em> con la máxima precisión industrial.
          </h2>
        </div>

        <div className="mt-20 md:mt-24 flex justify-center">
          <ToggleGroup type="single" value={activeCategory} onValueChange={v => v && setActiveCategory(v)} className="flex flex-wrap justify-center gap-2 bg-transparent">
            {categories.map((cat, i) => <ToggleGroupItem data-index={i} key={cat.id} value={cat.id} className="font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-2.5 h-auto rounded-md border border-border bg-transparent text-muted-foreground hover:text-primary hover:bg-primary-100 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary">
                {cat.label}
              </ToggleGroupItem>)}
          </ToggleGroup>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {filtered.map((product, i) => <ProductCard data-index={i} key={product.id} product={product} />)}
        </div>
      </div>
    </section>;
}
function ProductCard({
  product,
  ...rest
}: {
  product: (typeof products)[number];
  [key: string]: any;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <Card className="group bg-card text-card-foreground rounded-lg shadow-md hover:shadow-xl transition-shadow duration-500 overflow-hidden border border-border py-0 gap-0">
        <div className="p-5">
          <div className="relative overflow-hidden rounded-md bg-muted">
            <AspectRatio ratio={4 / 5}>
              <img src={product.image} data-wvc-srcset={`${product.image}-wvc-srcset`} data-wvc-sizes={`${product.image}-wvc-sizes`} alt={product.name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[400ms] group-hover:opacity-0" />
              <img src={product.imageHover} data-wvc-srcset={`${product.imageHover}-wvc-srcset`} data-wvc-sizes={`${product.imageHover}-wvc-sizes`} alt={`${product.name} en uso`} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-[400ms] group-hover:opacity-100" />
            </AspectRatio>
          </div>
        </div>

        <CardContent className="px-7 pb-7 pt-2">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase mb-4 text-primary">
            {product.eyebrow}
          </p>
          <h3 className="font-default text-2xl font-bold text-foreground leading-tight mb-3">
            {product.name}
          </h3>
          <p className="font-default text-base leading-relaxed text-muted-foreground mb-8 min-h-[3rem]">
            {product.description}
          </p>

          <div className="flex items-center justify-between pt-2">
            <span className="font-default text-2xl font-bold text-secondary">
              {product.price}
            </span>

            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" className="group/btn flex items-center gap-3 px-0 hover:bg-transparent">
                  <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-primary">
                    Ficha Técnica
                  </span>
                  <span className="flex items-center justify-center w-9 h-9 rounded-md transition-transform duration-300 group-hover/btn:translate-x-1 bg-primary text-primary-foreground">
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card text-card-foreground border-border rounded-lg">
                <DialogHeader>
                  <p className="font-mono text-[11px] tracking-[0.2em] uppercase mb-2 text-primary">
                    {product.eyebrow}
                  </p>
                  <DialogTitle className="font-default text-3xl font-bold text-foreground">
                    {product.name}
                  </DialogTitle>
                  <DialogDescription className="font-default text-base text-muted-foreground leading-relaxed pt-2">
                    {product.description}
                  </DialogDescription>
                </DialogHeader>

                <div className="border-t border-border pt-5 mt-2">
                  <p className="font-mono text-[11px] tracking-[0.2em] uppercase mb-3 text-accent">
                    Especificaciones Técnicas
                  </p>
                  <p className="font-default text-sm text-foreground/80 leading-relaxed">
                    {product.specs}
                  </p>
                </div>

                <DialogFooter className="flex items-center sm:justify-between gap-4 pt-4 border-t border-border">
                  <span className="font-default text-2xl font-bold text-secondary">
                    {product.price}
                  </span>
                  <Button asChild className="rounded-md px-6 bg-primary text-primary-foreground hover:bg-primary-600">
                    <Link to="/ProductsPage">
                      <span className="font-mono text-[11px] tracking-[0.25em] uppercase">
                        Consultar
                      </span>
                    </Link>
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    </div>;
}